import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NotificationService } from 'src/app/utils/notification.service';

export type MiniUploadVariant = 'square' | 'pill';

@Component({
  selector: 'app-mini-upload',
  templateUrl: './mini-upload.component.html',
  styleUrls: ['./mini-upload.component.css'],
})
export class MiniUploadComponent {
  @Input() variant: MiniUploadVariant = 'square';
  @Input() label: string = '';
  @Input() uploadedLabel: string = '';
  @Input() value: string = '';
  @Input() disabled: boolean = false;
  @Input() accept: string = 'image/*,application/pdf';
  @Input() maxSizeMB: number = 2;
  @Input() acceptHint: string = '';
  @Input() hideInternalDelete: boolean = false;
  @Input() hidePlaceholderIcon: boolean = false;
  @Input() showFileName: boolean = false;
  @Input() iconSrc: string = '';
  @Input() placeholderIconSrc: string = '';

  @Output() fileSelected = new EventEmitter<string>();
  @Output() fileRemoved = new EventEmitter<void>();

  public selectedFileName: string = '';

  constructor(private toastr: NotificationService) {}

  public onChange(ev: any) {
    const file = ev?.target?.files?.[0];
    if (!file) return;
    if (file.size > this.maxSizeMB * 1024 * 1024) {
      this.toastr.error(`File should not exceed ${this.maxSizeMB}MB`);
      ev.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.selectedFileName = file.name;
      this.fileSelected.emit(e.target.result);
      ev.target.value = '';
    };
    reader.readAsDataURL(file);
  }

  public onRemove(ev: Event) {
    ev.preventDefault();
    ev.stopPropagation();
    this.selectedFileName = '';
    this.fileRemoved.emit();
  }

  public get effectiveUploadedLabel(): string {
    if (this.showFileName && this.selectedFileName) return this.selectedFileName;
    if (this.uploadedLabel) return this.uploadedLabel;
    // Strip a leading "Upload " (case-insensitive) so the call-to-action
    // "Upload Signature" becomes just "Signature" once a file is present.
    return (this.label || '').replace(/^upload\s+/i, '');
  }

  public get formatsHint(): string {
    if (this.acceptHint) return this.acceptHint;
    const a = (this.accept || '').toLowerCase();
    const exts: string[] = [];
    if (a.includes('image/*') || a.includes('image/png')) exts.push('.png');
    if (a.includes('image/*') || a.includes('image/jpeg')) exts.push('.jpg');
    if (a.includes('image/*') || a.includes('image/svg')) exts.push('.svg');
    if (a.includes('application/pdf')) exts.push('.pdf');
    if (a.includes('image/bmp')) exts.push('.bmp');
    const list = exts.length ? exts.join(', ') : 'Any';
    return `${list} (Max. ${this.maxSizeMB}MB)`;
  }

  public isImage(value: string): boolean {
    if (!value) return false;
    if (value.startsWith('data:image/')) return true;
    if (value.startsWith('data:application/pdf')) return false;
    return /\.(jpg|jpeg|png|svg|gif|webp)(\?|$)/i.test(value);
  }
}
